/* eslint-disable import/no-cycle */
import chartsService from '@/services/chartsService';

const state = {
  charts: [], // Array of charts with assessmentId
  linkableAssessments: [],
  loading: false,
  error: null,
};

const getters = {
  getChartByAssessmentId: (storeState) => (assessmentId) => {
    if (!Array.isArray(storeState.charts)) {
      return [];
    }
    return storeState.charts.filter(
      (chart) => chart.assessmentId === assessmentId,
    );
  },
  getLinkableAssessments: (storeState) => storeState.linkableAssessments,
  isLoading: (storeState) => storeState.loading,
  getError: (storeState) => storeState.error,
};
const actions = {
  async fetchCharts({ commit, dispatch }) {
    commit('SET_LOADING', true);
    try {
      const response = await chartsService.getAllCharts();
      commit('SET_CHARTS', response.data);
    } catch (error) {
      commit('SET_ERROR', error.message);
      dispatch(
        'notifications/showToast',
        {
          detail: 'Failed to fetch charts',
          severity: 'error',
        },
        { root: true },
      );
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async fetchChartByAssessmentId({ commit, dispatch }, assessmentId) {
    commit('SET_LOADING', true);
    try {
      const response = await chartsService.getChartByAssessmentId(assessmentId);
      commit('SET_CHARTS', response.data);
    } catch (error) {
      commit('SET_ERROR', error.message);
      dispatch(
        'notifications/showToast',
        {
          detail: 'Failed to fetch chart',
          severity: 'error',
        },
        { root: true },
      );
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async fetchChartById({ commit }, chartId) {
    commit('SET_LOADING', true);
    try {
      const chart = await chartsService.getChartById(chartId);
      commit('SET_CHART', chart);
    } catch (error) {
      commit('SET_ERROR', error.message);
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async saveChart({ commit, dispatch }, chart) {
    commit('SET_LOADING', true);
    try {
      let savedChart;
      if (!chart._id) {
        savedChart = await chartsService.createChart(chart);
        dispatch(
          'notifications/showToast',
          {
            detail: 'Chart created successfully',
            severity: 'success',
          },
          { root: true },
        );
      } else {
        // Remove _id, __v and nested _id fields before sending to API
        const chartToUpdate = { ...chart };
        delete chartToUpdate._id;
        delete chartToUpdate.__v;
        if (chartToUpdate.data?.datasets) {
          chartToUpdate.data.datasets = chartToUpdate.data.datasets.map((dataset) => {
            const { _id, ...datasetWithoutId } = dataset;
            return datasetWithoutId;
          });
        }
        savedChart = await chartsService.updateChart(chart._id, chartToUpdate);
        dispatch(
          'notifications/showToast',
          {
            detail: 'Chart updated successfully',
            severity: 'success',
          },
          { root: true },
        );
      }
      // Update the store with the saved chart
      commit('SET_CHART', savedChart);
    } catch (error) {
      commit('SET_ERROR', error.message);
      dispatch(
        'notifications/showToast',
        {
          detail: 'Failed to save chart',
          severity: 'error',
        },
        { root: true },
      );
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async deleteChart({ commit, dispatch }, chartId) {
    commit('SET_LOADING', true);
    try {
      await chartsService.deleteChart(chartId);
      commit('REMOVE_CHART', chartId);
      dispatch(
        'notifications/showToast',
        {
          detail: 'Chart deleted successfully',
          severity: 'success',
        },
        { root: true },
      );
    } catch (error) {
      commit('SET_ERROR', error.message);
      dispatch(
        'notifications/showToast',
        {
          detail: 'Failed to delete chart',
          severity: 'error',
        },
        { root: true },
      );
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async fetchLinkableAssessments({ commit, dispatch }, { assessmentId, dimensions }) {
    commit('SET_LOADING', true);
    try {
      const assessments = await chartsService.getLinkableAssessments(assessmentId, dimensions);
      commit('SET_LINKABLE_ASSESSMENTS', assessments);
    } catch (error) {
      commit('SET_ERROR', error.message);
      dispatch(
        'notifications/showToast',
        {
          detail: 'Failed to fetch linkable assessments',
          severity: 'error',
        },
        { root: true },
      );
    } finally {
      commit('SET_LOADING', false);
    }
  },
};

const mutations = {
  SET_CHARTS(storeState, charts) {
    // eslint-disable-next-line no-param-reassign
    storeState.charts = charts;
  },
  SET_CHART(storeState, chart) {
    const existingChartIndex = storeState.charts.findIndex(
      (c) => c._id === chart._id ||
        (c.assessmentId?._id === chart.assessmentId?._id && c.title?.en === chart.title?.en),
    );
    if (existingChartIndex !== -1) {
      console.log('updating chart ...');
      // Update existing chart
      // eslint-disable-next-line no-param-reassign
      storeState.charts.splice(existingChartIndex, 1, chart);
    } else {
      console.log('adding chart ...');
      // Add new chart
      // eslint-disable-next-line no-param-reassign
      storeState.charts.push(chart);
    }
  },
  REMOVE_CHART(storeState, chartId) {
    // eslint-disable-next-line no-param-reassign
    storeState.charts = storeState.charts.filter((chart) => chart._id !== chartId);
  },
  SET_LINKABLE_ASSESSMENTS(storeState, assessments) {
    // eslint-disable-next-line no-param-reassign
    storeState.linkableAssessments = assessments;
  },
  SET_LOADING(storeState, loading) {
    // eslint-disable-next-line no-param-reassign
    storeState.loading = loading;
  },
  SET_ERROR(storeState, error) {
    // eslint-disable-next-line no-param-reassign
    storeState.error = error;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
